import { environment } from 'src/environments/environment';
import { BehaviorSubject, interval, Subject, Subscription } from 'rxjs';
import { Injectable } from '@angular/core';
import { ClientContextService } from '@sdbn/appclient';
import { HttpService } from '@sdbn/appclient';
import { Unit } from '@sdbn/appclient';
import { StationUsage } from '../../tmc-yard/client/model/station-usage.model';
import { map } from 'rxjs/operators';
import { YardConfiguration } from '../../tmc-yard/client/model/yard-configuration';
import { Yard } from '../../tmc-yard/client/model/yard';
import { MessagingProvider } from '../../tmc-yard/client/model/messagingprovider';
import { Station } from '../../tmc-yard/client/model/station';
import { StationGroup } from '../../tmc-yard/client/model/station-group';
import { StationSet } from '../../tmc-yard/client/model/station-set';
import { OneTimeSubject } from '@sdbn/common';

@Injectable({
  providedIn: 'root'
})
export class TmcContextService {

  stationUsages = new Array<StationUsage>();
  stationUsagesRefreshInterval = 30 * 1000;
  stationUsagesRefreshTimerSubscription: Subscription;

  private stationUsagesSubject = new Subject<string>();
  public stationUsagesUpdated$ = this.stationUsagesSubject.asObservable();

  updated = new BehaviorSubject<YardConfiguration>(null);

  private yardConfiguration: YardConfiguration;
  public yardConfigurationInitialized = new OneTimeSubject();

  constructor(
    private clientContextService: ClientContextService,
    private httpService: HttpService) {

    this.clientContextService.unitUpdated$.subscribe(unit => {
      if (unit != null) {
        this.loadYardConfig(unit);
      }
    });
    this.registerStationUsageRefreshTimer();
  }

  private registerStationUsageRefreshTimer() {

    if (this.stationUsagesRefreshTimerSubscription) {
      this.stationUsagesRefreshTimerSubscription.unsubscribe();
      this.stationUsagesRefreshTimerSubscription = null;
    }

    if (environment.autoreload) {
      this.stationUsagesRefreshTimerSubscription = interval(this.stationUsagesRefreshInterval).subscribe(n => {
        this.updateStationUsages();
      });
    }
  }

  public getYard(): Yard {
    const unit = this.clientContextService.getUnit();
    const yard = new Yard();
    yard.id = unit.id;
    yard.key = unit.key;
    yard.name = unit.name;
    yard.timezone = unit.timezone;
    return yard;
  }

  public getYardKey(): string {
    const yard = this.getYard();
    if (yard) {
      return yard.key;
    }
    return null;
  }

  public getYardConfiguration(): YardConfiguration {
    return this.yardConfiguration;
  }

  public getAvailableMessagingProvider(): Array<MessagingProvider> {
    if (this.yardConfiguration != null && this.yardConfiguration.messagingProvider != null) {
      return this.yardConfiguration.messagingProvider;
    }
    return new Array<MessagingProvider>();
  }

  private loadYardConfig(unit: Unit) {
    this.httpService.postWithContext<YardConfiguration>(environment.clientConfiguration + '/yardconfig', {}).subscribe(yardConfig => {
      console.log('TmcContextService.loadYardConfig', yardConfig);
      this.yardConfiguration = new YardConfiguration(yardConfig);
      this.yardConfigurationInitialized.next(this.yardConfiguration);
      this.updated.next(this.yardConfiguration);
      this.stationUsages.splice(0, this.stationUsages.length);
      // Nach dem Wechsel des Yard muss neu geladen werden!
      this.forceRefreshOfStationUsages();
    });
  }

  public forceRefreshOfStationUsages() {

    // Stop Timer
    if (this.stationUsagesRefreshTimerSubscription) {
      this.stationUsagesRefreshTimerSubscription.unsubscribe();
      this.stationUsagesRefreshTimerSubscription = null;
    }

    // Refresh
    this.updateStationUsages();

    // Start Timer
    if (environment.autoreload) {
      this.stationUsagesRefreshTimerSubscription = interval(this.stationUsagesRefreshInterval).subscribe(n => {
        this.updateStationUsages();
      });
    }
  }

  private updateStationUsages(): void {
    this.stationUsagesSubject.next('begin');
    this.httpService.postWithContext<StationUsage[]>(environment.clientConfiguration + '/station-usage', {}).pipe(
      map(
        r => r.map(s => new StationUsage(s))
      )
    ).subscribe(
      stationUsages => {
        this.mergeStationUsages(stationUsages);
        this.stationUsagesSubject.next('end');
      },
      error => {
        console.error(error);
      }
    );
  }

  private mergeStationUsages(stationUsages: Array<StationUsage>) {
    const stationUsagesByStationId = new Map<number, StationUsage>();
    stationUsages.forEach(s => stationUsagesByStationId.set(s.stationId, s));

    for (const station of this.yardConfiguration.stations) {
      const detailsForStation = stationUsagesByStationId.get(station.id);
      if (detailsForStation != null) {
        station.stationUsage.updateStationUsageDetails(detailsForStation);
        station.stationUsage.changed.next(true);
      } else {
        station.stationUsage.updateStationUsageDetails({});
        station.stationUsage.changed.next(true);
      }
    }
  }

  public findStationById(stationId: number): Station {
    if (this.yardConfiguration != null) {
      return this.yardConfiguration.stations.find(s => s.id === stationId);
    }
    return null;
  }

  public findStationByIdentifier(stationIdentifier: string): Station {
    if (this.yardConfiguration != null) {
      return this.yardConfiguration.stations.find(s => s.identifier === stationIdentifier);
    }
    return null;
  }

  public findStationByExternalKey(externalKey: string): Station {
    if (this.yardConfiguration != null) {
      return this.yardConfiguration.stations.find(s => s.externalKey === externalKey);
    }
    return null;
  }

  public findStationGroupById(stationGroupId: number): StationGroup {
    if (this.yardConfiguration != null) {
      return this.yardConfiguration.stationGroups.find(s => s.id === stationGroupId);
    }
    return null;
  }

  public findStationGroupByExternalKey(externalKey: string): StationGroup {
    if (this.yardConfiguration != null) {
      return this.yardConfiguration.stationGroups.find(s => s.externalKey === externalKey);
    }
    return null;
  }

  public findStationSetById(stationSetId: number): StationSet {
    if (this.yardConfiguration != null) {
      for (const sg of this.yardConfiguration.stationGroups) {
        const set = sg.stationSets.find(s => s.id === stationSetId);
        if (set != null) {
          return set;
        }
      }
    }
    return null;
  }

  public findStationSetByExternalKey(externalKey: string): StationSet {
    if (this.yardConfiguration != null) {
      for (const sg of this.yardConfiguration.stationGroups) {
        const set = sg.stationSets.find(s => s.externalKey === externalKey);
        if (set != null) {
          return set;
        }
      }
    }
    return null;
  }

}
